import { useCallback, useMemo, useRef } from 'react';

import { RecordedChunk, StopIntent } from './types';

type UseAudioRecorderControllerOptions = {
  onInterrupted: () => Promise<void> | void;
  onError: (error: unknown) => void;
  onStreamReady: (stream: MediaStream) => void;
  getChunkDurationSeconds: () => number;
};

type StartRecorderOptions = {
  onChunk: (chunk: RecordedChunk) => Promise<void> | void;
};

const chooseSupportedMimeType = () => {
  const supportedTypes = [
    'audio/webm',
    'audio/webm;codecs=opus',
    'audio/ogg',
    'audio/mp4',
    'audio/wav'
  ];

  return supportedTypes.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
};

export const useAudioRecorderController = ({
  onInterrupted,
  onError,
  onStreamReady,
  getChunkDurationSeconds
}: UseAudioRecorderControllerOptions) => {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopIntentRef = useRef<StopIntent>(null);
  const stopResolverRef = useRef<(() => void) | null>(null);

  const cleanup = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => {
      track.onended = null;
      track.stop();
    });
    streamRef.current = null;
    recorderRef.current = null;
    stopResolverRef.current = null;
  }, []);

  const requestStream = useCallback(async () => {
    stopIntentRef.current = null;

    if (streamRef.current?.active) {
      return streamRef.current;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        autoGainControl: false,
        echoCancellation: false,
        noiseSuppression: false
      }
    });

    streamRef.current = stream;
    onStreamReady(stream);

    return stream;
  }, [onStreamReady]);

  const start = useCallback(
    async ({ onChunk }: StartRecorderOptions) => {
      stopIntentRef.current = null;

      const stream = await requestStream();
      const mimeType = chooseSupportedMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      recorder.ondataavailable = async (event) => {
        if (event.data.size === 0) return;

        await onChunk({
          blob: event.data,
          mimeType: event.data.type || mimeType,
          createdAt: Date.now(),
          durationSeconds: getChunkDurationSeconds()
        });
      };

      recorder.onstop = () => {
        stopResolverRef.current?.();
        stopResolverRef.current = null;
      };

      recorder.onerror = onError;

      stream.getTracks().forEach((track) => {
        track.onended = async () => {
          if (stopIntentRef.current) return;

          stopIntentRef.current = 'interruption';
          await onInterrupted();
        };
      });

      recorderRef.current = recorder;

      recorder.start(10000);

      return { stream, mimeType };
    },
    [getChunkDurationSeconds, onError, onInterrupted, requestStream]
  );

  const requestFinalChunkAndStop = useCallback((intent: StopIntent) => {
    stopIntentRef.current = intent;

    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      stopResolverRef.current = resolve;
      recorder.requestData();
      recorder.stop();
    });
  }, []);

  const pause = useCallback(() => {
    stopIntentRef.current = 'pause';

    const recorder = recorderRef.current;
    if (recorder?.state !== 'recording') return;

    recorder.requestData();
    recorder.pause();
  }, []);

  const resume = useCallback(() => {
    stopIntentRef.current = null;

    const recorder = recorderRef.current;
    if (recorder?.state !== 'paused') return;

    recorder.resume();
  }, []);

  const hasRecorder = useCallback(() => recorderRef.current != null, []);
  const isPaused = useCallback(() => recorderRef.current?.state === 'paused', []);

  const submit = useCallback(() => requestFinalChunkAndStop('submit'), [requestFinalChunkAndStop]);
  const discard = useCallback(() => {
    stopIntentRef.current = 'discard';

    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      stopResolverRef.current = resolve;
      recorder.ondataavailable = null;
      recorder.stop();
    });
  }, []);

  return useMemo(
    () => ({
      start,
      requestStream,
      pause,
      resume,
      submit,
      discard,
      cleanup,
      hasRecorder,
      isPaused
    }),
    [cleanup, discard, hasRecorder, isPaused, pause, requestStream, resume, start, submit]
  );
};
